import { useMemo } from 'react';
import { format } from 'date-fns';
import { parseBookingDate, normalizeTimeTo24h } from '../helpers';
import type { Booking } from '../types';

/**
 * Hook to extract and memoize current booking date and time information.
 *
 * Handles parsing of booking date from various formats and normalizes
 * the booking time to 24-hour format for consistent comparison.
 *
 * @param booking - The booking object containing date and time information
 * @returns Object containing formatted date string and normalized time
 *
 * @example
 * ```typescript
 * const { currentBookingDateStr, currentBookingTime24 } = useCurrentBookingInfo(booking);
 * // currentBookingDateStr: '2024-02-14' or null
 * // currentBookingTime24: '10:00' or '14:30'
 * ```
 */
export const useCurrentBookingInfo = (booking: Booking) => {
  /**
   * Parse and format the current booking date to 'yyyy-MM-dd' format.
   * Returns null if the date cannot be parsed.
   */
  const currentBookingDateStr = useMemo(() => {
    const bookingDate = parseBookingDate(booking.date || booking.formattedDate);
    return bookingDate ? format(bookingDate, 'yyyy-MM-dd') : null;
  }, [booking.date, booking.formattedDate]);

  /**
   * Normalize the booking time to 24-hour format (e.g., '10:00 AM' -> '10:00').
   * This ensures consistent time comparison regardless of input format.
   */
  const currentBookingTime24 = useMemo(
    () => normalizeTimeTo24h(booking.formattedTime),
    [booking.formattedTime]
  );

  return { currentBookingDateStr, currentBookingTime24 };
};
